/** 讨论组：从工作会话发起；成员真实登录发言；AI 节流刷新结论；归档后落待发邮件与云空间结论文档。 */

export interface DiscussionCandidate { id: string; name: string; nick?: string; dept: string }
export interface DiscussionRow {
  id: string
  /** temp=临时讨论（形成结论后归档）；fixed=固定群聊（常设，结论常驻刷新不归档） */
  kind: 'temp' | 'fixed'
  title: string
  status: 'active' | 'archived'
  updated_at: string
  concluded_at: string | null
  conclusion: string
  member_count: number
  unread: number
  last_message_at: string | null
  /** 固定群聊是否已开通群空间（云空间「群聊文件」页签据此判断展示） */
  space_enabled: boolean
}
export interface DiscussionMember { id: string; name: string; nick?: string; dept: string; role: 'creator' | 'admin' | 'member'; lastReadAt: string | null }
export interface DiscussionAttachment { id: string; name: string; sizeBytes: number; image: boolean }
/** 群内子话题：成员可发起；open 可发言，closed 只读保留 */
export interface DiscussionThread { id: string; title: string; status: 'open' | 'closed'; creatorId: string; creatorName: string; creatorNick?: string; msgCount: number; lastMessageAt: string | null; createdAt: string }
export interface DiscussionMention { id: string; name: string; nick?: string }
/** 引用回复摘要：被引用消息可能已不在本页，按服务端快照展示；deleted=被引用消息已删除（预览为空） */
export interface DiscussionReplyTo { id: string; authorName: string; authorNick?: string; preview: string; hasFile: boolean; deleted?: boolean }
export interface DiscussionMessage { id: string; authorId: string | null; authorName: string; authorNick?: string; kind: 'say' | 'note'; body: string; createdAt: string; mine: boolean; attachments: DiscussionAttachment[]; threadId: string | null; threadTitle: string; replyTo: DiscussionReplyTo | null; mentions: DiscussionMention[]; mentionedMe: boolean; deleted?: boolean; deletedByName?: string; favorited?: boolean; remindAt?: string | null }
export type DiscussionSearchType = 'all' | 'file' | 'image' | 'link'
export interface DiscussionSearchPage { type: DiscussionSearchType; q: string; hasMore: boolean; results: DiscussionMessage[] }
export interface DiscussionMail { id: string; toName: string; toNick?: string; subject: string; body: string; status: string; createdAt: string }
export interface DiscussionAnnouncement { id: string; body: string; authorName: string; authorNick?: string; createdAt: string }
export interface DiscussionPin { messageId: string; authorName: string; authorNick?: string; preview: string; hasFile: boolean; createdAt: string }
/** 群聊文件：已开通群空间的固定群中的发言附件；下载走讨论组鉴权路由 */
export interface DiscussionSpaceFile { id: string; name: string; sizeBytes: number; image: boolean; discussionId: string; discussionTitle: string; authorName: string; authorNick?: string; createdAt: string }
/** 我的收藏条目：deleted=原消息已被删除（预览为空，仅作墓碑展示） */
export interface DiscussionFavorite { messageId: string; discussionId: string; discussionTitle: string; authorName: string; authorNick?: string; preview: string; hasFile: boolean; deleted: boolean; createdAt: string; favoritedAt: string }
export interface DiscussionDetail {
  id: string
  /** temp=临时讨论；fixed=固定群聊（支持管理员/公告/置顶/群空间） */
  kind: 'temp' | 'fixed'
  title: string
  background: string
  conclusion: string | null
  conclusionAt: string | null
  status: 'active' | 'archived'
  creatorId: string
  sourceSessionId: string | null
  summaryFile: { id: string; name: string } | null
  createdAt: string
  updatedAt: string
  concludedAt: string | null
  /** 本人在群内的角色 */
  myRole: 'creator' | 'admin' | 'member'
  /** 当前公告（最新一条）；未发布过为 null */
  announcement: DiscussionAnnouncement | null
  /** 置顶消息（新→旧） */
  pins: DiscussionPin[]
  /** 群空间是否已开通 */
  spaceEnabled: boolean
  /** 群内子话题（新→旧），含已结束 */
  threads: DiscussionThread[]
  members: DiscussionMember[]
  messages: DiscussionMessage[]
  hasMore: boolean
  mails: DiscussionMail[]
}

async function request<T>(path: string, body?: unknown): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 20000)
  try {
    const r = await fetch('/api/discussions' + path, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
      cache: 'no-store',
    })
    const data = await r.json()
    if (!r.ok || data.code !== 0) throw Error(data.message || '讨论请求未完成')
    return data.data as T
  } finally { clearTimeout(timer) }
}

export const discussionRepo = {
  candidates: (q = '') => request<DiscussionCandidate[]>('/candidates?q=' + encodeURIComponent(q)),
  list: () => request<DiscussionRow[]>(''),
  detail: (id: string, before?: string, beforeId?: string, threadId?: string) =>
    request<DiscussionDetail>('/' + encodeURIComponent(id) + (before ? '?before=' + encodeURIComponent(before) + '&beforeId=' + encodeURIComponent(beforeId || '') + (threadId ? '&thread=' + encodeURIComponent(threadId) : '') : (threadId ? '?thread=' + encodeURIComponent(threadId) : ''))),
  create: (sourceSessionId: string, title: string, memberIds: string[]) =>
    request<DiscussionDetail>('', { sourceSessionId, title, memberIds }),
  say: (id: string, text: string, fileIds: string[] = [], opts: { threadId?: string; replyToId?: string; mentionIds?: string[] } = {}) =>
    request<{ id: string; createdAt: string; replayed: boolean }>('/' + encodeURIComponent(id) + '/messages', { requestKey: crypto.randomUUID(), body: text, ...(fileIds.length ? { fileIds } : {}), ...(opts.threadId ? { threadId: opts.threadId } : {}), ...(opts.replyToId ? { replyToId: opts.replyToId } : {}), ...(opts.mentionIds?.length ? { mentionIds: opts.mentionIds } : {}) }),
  /** 创建子话题：任一在群成员可发起 */
  createThread: (id: string, title: string) =>
    request<{ id: string; title: string; status: 'open' }>('/' + encodeURIComponent(id) + '/threads', { title }),
  /** 结束子话题：话题发起人、群主或（固定群）管理员 */
  closeThread: (id: string, threadId: string) =>
    request<{ threadId: string; status: 'closed' }>('/' + encodeURIComponent(id) + '/threads/close', { threadId }),
  addMembers: (id: string, memberIds: string[]) =>
    request<{ added: string[] }>('/' + encodeURIComponent(id) + '/members', { memberIds }),
  search: (id: string, q: string, type: DiscussionSearchType = 'all', before?: string, beforeId?: string) =>
    request<DiscussionSearchPage>('/' + encodeURIComponent(id) + '/search?q=' + encodeURIComponent(q) + '&type=' + type + (before ? '&before=' + encodeURIComponent(before) + '&beforeId=' + encodeURIComponent(beforeId || '') : '')),
  fileDownloadUrl: (id: string, fileId: string) => '/api/discussions/' + encodeURIComponent(id) + '/files/' + encodeURIComponent(fileId) + '/download',
  summarize: (id: string) =>
    request<{ conclusion: string | null; conclusionAt: string | null; status: 'active' | 'archived' }>('/' + encodeURIComponent(id) + '/summarize', {}),
  markRead: (id: string) => request<{ ok: true }>('/' + encodeURIComponent(id) + '/read', {}),
  /* ---- 固定群聊管理：仅 kind='fixed' 可用 ---- */
  /** 群主设置/取消管理员 */
  setAdmin: (id: string, personId: string, admin: boolean) =>
    request<{ personId: string; role: 'admin' | 'member' }>('/' + encodeURIComponent(id) + '/admins', { personId, admin }),
  /** 群主或管理员发布群公告（≤2000字节） */
  publishAnnouncement: (id: string, body: string) =>
    request<{ id: string }>('/' + encodeURIComponent(id) + '/announcement', { body }),
  /** 群主或管理员置顶/取消置顶消息（含文件消息） */
  setPin: (id: string, messageId: string, pinned: boolean) =>
    request<{ messageId: string; pinned: boolean }>('/' + encodeURIComponent(id) + '/pins', { messageId, pinned }),
  /** 群主或管理员开通/关闭群空间 */
  setSpace: (id: string, enabled: boolean) =>
    request<{ enabled: boolean }>('/' + encodeURIComponent(id) + '/space', { enabled }),
  /** 群聊文件：我所在的已开通群空间的固定群的全部附件 */
  spaceFiles: () => request<DiscussionSpaceFile[]>('/space-files'),
  /* ---- 消息动作：删除/收藏/提醒/转发 ---- */
  /** 删除消息：作者本人可删；群主与固定群管理员可删任何发言；原位墓碑展示 */
  removeMessage: (id: string, messageId: string) =>
    request<{ messageId: string; deleted: boolean }>('/' + encodeURIComponent(id) + '/messages/delete', { messageId }),
  /** 收藏/取消收藏一条发言；收藏在云空间「我的收藏」查看 */
  setFavorite: (id: string, messageId: string, favorite: boolean) =>
    request<{ messageId: string; favorite: boolean }>('/' + encodeURIComponent(id) + '/messages/favorite', { messageId, favorite }),
  /** 我的收藏列表（按收藏时间倒序） */
  favorites: () => request<DiscussionFavorite[]>('/favorites'),
  /** 设定发言提醒（remindAt ISO 时间）；传 null 取消 */
  setReminder: (id: string, messageId: string, remindAt: string | null) =>
    request<{ messageId: string; remindAt: string | null }>('/' + encodeURIComponent(id) + '/messages/remind', { messageId, remindAt }),
  /** 转发消息到另一个讨论组（我加入的进行中讨论，一次最多20条） */
  forward: (id: string, messageIds: string[], targetDiscussionId: string) =>
    request<{ forwarded: number; targetDiscussionId: string }>('/' + encodeURIComponent(id) + '/forward', { messageIds, targetDiscussionId }),
}
